import { useRef, useMemo } from 'react'
import { parseDate, diffDays, calcRange, buildMonthTicks, buildWeekTicks, GANTT_COLORS } from './ganttUtils'
import type { CommessaStrategia } from './GanttCommesse'

interface Assignment {
  commessa_id: string
  commessa_numero: string
  commessa_titolo: string
  fase_id: string
  fase_titolo: string
  start: string
  fine: string
  giorni: number
  completata: boolean
  in_ritardo: boolean
  color_idx: number
}

interface Lane { assignments: Assignment[] }

interface PersonaRow {
  id: string
  nome: string
  lanes: Lane[]          // interval-scheduled lanes, no overlap within a lane
  total_giorni: number
}

interface Props {
  commesse: CommessaStrategia[]
  personale: Record<string, string>
  zoom: 'mesi' | 'settimane'
}

const LEFT_W = 200
const LANE_H  = 28   // height of each lane
const PERSON_PAD = 6 // vertical padding around lanes per person
const HEADER_H = 48
const today = new Date(); today.setHours(0, 0, 0, 0)

/** Interval scheduling: assign each assignment to a lane so no two in the same lane overlap */
function scheduleLanes(assignments: Assignment[]): Lane[] {
  const sorted = [...assignments].sort((a, b) => a.start.localeCompare(b.start))
  const lanes: { end: string; items: Assignment[] }[] = []
  for (const a of sorted) {
    let placed = false
    for (const lane of lanes) {
      if (lane.end <= a.start) {
        lane.items.push(a)
        lane.end = a.fine
        placed = true
        break
      }
    }
    if (!placed) lanes.push({ end: a.fine, items: [a] })
  }
  return lanes.map(l => ({ assignments: l.items }))
}

export default function GanttPersonale({ commesse, personale, zoom }: Props) {
  const rightRef = useRef<HTMLDivElement>(null)
  const leftRef  = useRef<HTMLDivElement>(null)
  const DAY_W = zoom === 'settimane' ? 28 : 11

  // Build person rows
  const rows = useMemo<PersonaRow[]>(() => {
    const map: Record<string, Assignment[]> = {}

    commesse.forEach((c, ci) => {
      // Usa le fasi come unità base — calcola date dal gantt
      c.fasi.forEach(f => {
        if (!f.data_inizio_prevista || !f.data_fine_prevista) return
        const giorni = diffDays(parseDate(f.data_inizio_prevista), parseDate(f.data_fine_prevista)) + 1
        const pids = new Set<string>([...f.partecipanti_ids, f.responsabile_id].filter(Boolean))
        pids.forEach(pid => {
          if (!map[pid]) map[pid] = []
          // Avoid duplicate fase
          if (!map[pid].find(a => a.fase_id === f.id)) {
            map[pid].push({
              commessa_id: c.id,
              commessa_numero: c.numero_commessa,
              commessa_titolo: c.titolo,
              fase_id: f.id,
              fase_titolo: f.titolo,
              start: f.data_inizio_prevista!,
              fine: f.data_fine_prevista!,
              giorni,
              completata: f.completata,
              in_ritardo: f.in_ritardo,
              color_idx: ci,
            })
          }
        })
      })
    })

    // Include ALL personale
    const allIds = new Set([...Object.keys(personale), ...Object.keys(map)])
    return Array.from(allIds)
      .map(id => {
        const assignments = map[id] || []
        const lanes = scheduleLanes(assignments)
        const total = assignments.reduce((s, a) => s + a.giorni, 0)
        return { id, nome: personale[id] || 'N/D', lanes, total_giorni: total }
      })
      .sort((a, b) => b.total_giorni - a.total_giorni || a.nome.localeCompare(b.nome))
  }, [commesse, personale])

  const allDates = useMemo(() =>
    rows.flatMap(r => r.lanes.flatMap(l => l.assignments.flatMap(a => [a.start, a.fine]))),
    [rows])

  const range       = useMemo(() => calcRange(allDates), [allDates])
  const monthTicks  = useMemo(() => range ? buildMonthTicks(range.minDate, range.maxDate) : [], [range])
  const weekTicks   = useMemo(() => range ? buildWeekTicks(range.minDate, range.maxDate) : [], [range])
  const totalDays   = range ? diffDays(range.minDate, range.maxDate) + 1 : 0
  const totalW      = totalDays * DAY_W
  const todayX      = range ? diffDays(range.minDate, today) * DAY_W : -1

  // Row heights: PERSON_PAD*2 + lanes * LANE_H
  const rowHeight = (r: PersonaRow) => Math.max(LANE_H, r.lanes.length * LANE_H) + PERSON_PAD * 2

  const syncScroll = () => {
    if (rightRef.current && leftRef.current)
      leftRef.current.scrollTop = rightRef.current.scrollTop
  }

  if (!range || rows.length === 0)
    return <div className="text-center text-gray-400 py-16">Nessun dato personale disponibile</div>

  return (
    <div className="flex border border-gray-200 rounded-lg overflow-hidden bg-white h-full">
      {/* Left panel */}
      <div className="flex-shrink-0 border-r border-gray-200 flex flex-col" style={{ width: LEFT_W }}>
        <div className="flex-shrink-0 bg-gray-50 border-b border-gray-200" style={{ height: HEADER_H }}>
          <div className="px-3 h-full flex items-end pb-1">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Personale</span>
          </div>
        </div>
        <div className="flex-1 overflow-hidden" ref={leftRef}>
          {rows.map(row => {
            const rh = rowHeight(row)
            const hasWork = row.lanes.length > 0 && row.total_giorni > 0
            const maxOverlap = row.lanes.length
            return (
              <div
                key={row.id}
                className="flex items-center px-3 border-b border-gray-100 gap-2"
                style={{ height: rh }}
              >
                <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold ${hasWork ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-300'}`}>
                  {row.nome.charAt(0)}
                </div>
                <div className="min-w-0">
                  <p className={`text-xs font-medium truncate ${hasWork ? 'text-gray-800' : 'text-gray-300'}`}>{row.nome}</p>
                  {hasWork ? (
                    <p className="text-xs text-gray-400">
                      {row.total_giorni}g
                      {maxOverlap > 1 && <span className="ml-1 text-amber-500 font-medium">×{maxOverlap}</span>}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-200">Nessun impegno</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 overflow-auto" ref={rightRef} onScroll={syncScroll}>
        <div style={{ width: Math.max(totalW, 600), minWidth: '100%' }}>

          {/* Sticky header */}
          <div className="sticky top-0 z-20 bg-gray-50 border-b border-gray-200 overflow-hidden" style={{ height: HEADER_H }}>
            <div className="relative h-6">
              {monthTicks.map((t, i) => (
                <div key={i} className="absolute top-0 h-full flex items-center border-l border-gray-200 px-1"
                     style={{ left: t.startDay * DAY_W, width: t.days * DAY_W }}>
                  <span className="text-xs font-medium text-gray-600 capitalize whitespace-nowrap truncate">{t.label}</span>
                </div>
              ))}
            </div>
            <div className="relative h-6 border-t border-gray-100">
              {zoom === 'settimane' && weekTicks.map((t, i) => (
                <div key={i} className="absolute top-0 h-full flex items-center border-l border-gray-100 px-0.5"
                     style={{ left: t.startDay * DAY_W }}>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{t.label}</span>
                </div>
              ))}
              {todayX >= 0 && <div className="absolute top-0 h-full w-px bg-red-400" style={{ left: todayX }} />}
            </div>
          </div>

          {/* Rows */}
          <div className="relative">
            {todayX >= 0 && <div className="absolute top-0 bottom-0 w-px bg-red-300 z-10 pointer-events-none" style={{ left: todayX }} />}
            {monthTicks.map((t, i) => (
              <div key={i} className="absolute top-0 bottom-0 w-px bg-gray-100" style={{ left: t.startDay * DAY_W }} />
            ))}

            {rows.map(row => {
              const rh = rowHeight(row)
              return (
                <div key={row.id} className="relative border-b border-gray-100" style={{ height: rh }}>
                  {row.lanes.length === 0 && (
                    <div className="h-full flex items-center px-2">
                      <span className="text-xs text-gray-200 italic">nessun impegno</span>
                    </div>
                  )}
                  {row.lanes.map((lane, li) => (
                    lane.assignments.map(a => {
                      const color = GANTT_COLORS[a.color_idx % GANTT_COLORS.length]
                      const startX = diffDays(range.minDate, parseDate(a.start)) * DAY_W
                      const endX   = diffDays(range.minDate, parseDate(a.fine)) * DAY_W
                      const barW   = Math.max(endX - startX, 8)
                      const top    = PERSON_PAD + li * LANE_H
                      const barColor = a.completata ? '#22c55e' : a.in_ritardo ? '#ef4444' : color.bar

                      return (
                        <div
                          key={a.fase_id}
                          className="absolute rounded flex items-center px-1 overflow-hidden cursor-default"
                          style={{
                            left: startX,
                            width: barW,
                            top: top + 2,
                            height: LANE_H - 4,
                            background: barColor,
                          }}
                          title={`${a.commessa_numero} — ${a.fase_titolo}\n${a.start} → ${a.fine}\n${a.giorni}g`}
                        >
                          {barW > 30 && (
                            <span className="text-white font-mono text-xs truncate leading-none" style={{ fontSize: 10 }}>
                              {a.commessa_numero}
                              {barW > 70 && <span className="opacity-80 ml-1">{a.giorni}g</span>}
                            </span>
                          )}
                          {barW > 8 && barW <= 30 && (
                            <span className="text-white font-bold" style={{ fontSize: 9 }}>{a.giorni}</span>
                          )}
                        </div>
                      )
                    })
                  ))}
                  {/* Overlap badge */}
                  {row.lanes.length > 1 && (
                    <div className="absolute right-1 top-1 bg-amber-100 text-amber-700 rounded text-xs font-bold px-1 leading-5" style={{ fontSize: 10 }}>
                      ×{row.lanes.length}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
